import React from 'react';
import { useTranslation } from '../hooks/useTranslation';
import { Link } from 'react-router-dom';
import { ROUTES } from '../lib/routes';
import SafeIcon from '../common/SafeIcon';
import { FiSearch, FiBook, FiUsers, FiMapPin, FiArrowRight } from 'react-icons/fi';
import { motion } from 'framer-motion';

const HomePage = () => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col w-full">
      {/* Hero Section */}
      <section className="bg-white pt-20 pb-24 md:pt-32 md:pb-40 border-b border-gray-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl md:text-6xl font-extrabold text-gray-900 tracking-tight mb-8 leading-[1.1]">
              {t('home.hero.title')}
            </h1>
            <p className="text-lg md:text-xl text-gray-500 max-w-2xl mx-auto mb-12 leading-relaxed">
              {t('home.hero.subtitle')}
            </p>
            
            {/* Main Search Bar */}
            <div className="max-w-2xl mx-auto relative group">
              <div className="absolute inset-y-0 left-0 pl-5 flex items-center pointer-events-none">
                <SafeIcon icon={FiSearch} className="h-5 w-5 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
              </div>
              <input
                type="text"
                className="block w-full pl-12 pr-4 py-5 border border-gray-200 rounded-2xl leading-5 bg-white placeholder-gray-400 focus:outline-none focus:ring-4 focus:ring-blue-50 focus:border-blue-500 sm:text-lg shadow-sm transition-all"
                placeholder={t('home.search.placeholder')}
              />
              <button className="absolute inset-y-2.5 right-2.5 px-8 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition-all shadow-md hover:shadow-lg active:scale-95">
                {t('common.search')}
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Quick Access Grid */}
      <section className="py-24 bg-[#f8fafc]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <FeatureCard 
              to={ROUTES.PUBLIC.RESOURCES}
              icon={FiBook}
              color="blue"
              title="Verified Resources"
              description="Access clinical explanations, preparation guides, and human-verified translations."
            />
            <FeatureCard 
              to={ROUTES.PUBLIC.CAREGIVER_HUB}
              icon={FiUsers}
              color="emerald"
              title="Caregiver Hub"
              description="Practical logistics and emotional support resources for those caring for loved ones."
            />
            <FeatureCard 
              to={ROUTES.PUBLIC.DIRECTORY}
              icon={FiMapPin}
              color="indigo"
              title="Support Directory"
              description="Find hospitals, cancer centers, NGOs, and specialized support services near you."
            />
          </div>
        </div>
      </section>
    </div>
  );
};

const FeatureCard = ({ to, icon, color, title, description }) => {
  const colors = {
    blue: 'bg-blue-50 text-blue-600',
    emerald: 'bg-emerald-50 text-emerald-600',
    indigo: 'bg-indigo-50 text-indigo-600'
  };

  return (
    <Link to={to} className="bg-white p-10 rounded-3xl shadow-sm border border-gray-100 hover:shadow-xl hover:-translate-y-1 transition-all group">
      <div className={`w-14 h-14 ${colors[color]} rounded-2xl flex items-center justify-center mb-8 group-hover:scale-110 transition-transform`}>
        <SafeIcon icon={icon} className="w-7 h-7" />
      </div>
      <h3 className="text-2xl font-bold text-gray-900 mb-3">{title}</h3>
      <p className="text-gray-500 leading-relaxed mb-6">{description}</p>
      <div className="flex items-center text-sm font-bold text-blue-600 group-hover:gap-2 transition-all">
        <span>Explore</span>
        <SafeIcon icon={FiArrowRight} className="ml-1" />
      </div>
    </Link>
  );
};

export default HomePage;